import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, Coffee, BookUser } from "lucide-react";
import {
  listWalks,
  createWalk,
  updateWalk,
  deleteWalk,
  type Walk,
  type WalkWrite,
} from "@/lib/walks-api";

const BADGE_OPTIONS = [
  "Community",
  "Self-paced",
  "Popular",
  "Foodie",
  "Premium",
  "Value",
  "Elegant",
];

type Draft = {
  id?: string;
  badge: string;
  title: string;
  description: string;
  meet_up: string;
  price: number;
  features: string[];
  is_breakfast: boolean;
  is_guide: boolean;
  is_active: boolean;
};

const EMPTY: Draft = {
  badge: "",
  title: "",
  description: "",
  meet_up: "",
  price: 0,
  features: [""],
  is_breakfast: false,
  is_guide: false,
  is_active: true,
};

const fmtPrice = (w: Walk) =>
  w.is_free || w.price === 0 ? "Free" : `₹${w.price.toLocaleString("en-IN")}`;

export const Route = createFileRoute("/admin/walks")({ component: WalksAdmin });

function WalksAdmin() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Draft | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-walks"],
    queryFn: listWalks,
  });

  // Blank feature rows are editor scaffolding, not content — drop them before saving.
  const toWrite = (d: Draft): WalkWrite => ({
    badge: d.badge.trim(),
    title: d.title.trim(),
    description: d.description.trim(),
    meet_up: d.meet_up.trim(),
    price: Number.isFinite(d.price) ? d.price : 0,
    features: d.features.map((f) => f.trim()).filter(Boolean),
    is_breakfast: d.is_breakfast,
    is_guide: d.is_guide,
    is_active: d.is_active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-walks"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: Draft) => createWalk(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Walk created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: Draft) => updateWalk(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Walk updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteWalk(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!edit.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };

  const remove = (id: string) => {
    if (confirm("Delete this walk?")) {
      deleteMut.mutate(id);
    }
  };

  const openEdit = (w: Walk) =>
    setEdit({
      id: w.id,
      badge: w.badge,
      title: w.title,
      description: w.description,
      meet_up: w.meet_up,
      price: w.price,
      features: w.features.length ? w.features : [""],
      is_breakfast: w.is_breakfast,
      is_guide: w.is_guide,
      is_active: w.is_active,
    });

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Walks</h2>
          <p className="text-sm text-muted-foreground">
            Guided-walk packages shown on the homepage
          </p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Add Walk
        </button>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      )}
      {isError && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load walks"}
        </div>
      )}
      {!isLoading && !isError && list.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No walks yet
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {list.map((w) => (
          <div key={w.id} className="flex flex-col rounded-2xl border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {w.badge ? (
                    <span className="rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      {w.badge}
                    </span>
                  ) : null}
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      w.is_active
                        ? "border-primary/30 bg-primary/15 text-primary"
                        : "border-border bg-muted text-muted-foreground"
                    }`}
                  >
                    {w.is_active ? "Active" : "Inactive"}
                  </span>
                  {w.is_breakfast ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      <Coffee className="h-3 w-3" /> Breakfast
                    </span>
                  ) : null}
                  {w.is_guide ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      <BookUser className="h-3 w-3" /> Guide
                    </span>
                  ) : null}
                </div>
                <h3 className="mt-2 font-serif text-lg font-bold">{w.title}</h3>
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  onClick={() => openEdit(w)}
                  title="Edit"
                  className="rounded-lg p-1.5 hover:bg-muted"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(w.id)}
                  title="Delete"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-1 font-serif text-2xl font-bold text-primary">{fmtPrice(w)}</div>

            {w.description ? (
              <p className="mt-2 text-sm text-muted-foreground">{w.description}</p>
            ) : null}

            {w.meet_up ? (
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Meetup: </span>
                {w.meet_up}
              </p>
            ) : null}

            {w.features.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {w.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {edit && (
        <Editor
          draft={edit}
          setDraft={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function Editor({
  draft,
  setDraft,
  onSave,
  onClose,
  saving,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const setFeature = (i: number, value: string) =>
    setDraft({ ...draft, features: draft.features.map((f, j) => (j === i ? value : f)) });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{draft.id ? "Edit" : "Add"} Walk</h3>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Badge">
            <select
              value={draft.badge}
              onChange={(e) => setDraft({ ...draft, badge: e.target.value })}
              className="inp"
            >
              <option value="">— None —</option>
              {/* Any legacy value already on the record is kept selectable so editing a walk
                  never silently rewrites its badge. */}
              {(BADGE_OPTIONS.includes(draft.badge) || !draft.badge
                ? BADGE_OPTIONS
                : [draft.badge, ...BADGE_OPTIONS]
              ).map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Price (₹)">
            <input
              type="number"
              min={0}
              step="any"
              value={draft.price}
              onChange={(e) => setDraft({ ...draft, price: e.target.value ? +e.target.value : 0 })}
              className="inp"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {draft.price === 0 ? "Shows as “Free”." : "Set 0 to mark this walk free."}
            </p>
          </Field>
          <Field label="Title" required full>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Volunteer Free Walk"
              className="inp"
            />
          </Field>
          <Field label="Description" full>
            <textarea
              rows={3}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Free walk guided by a community volunteer"
              className="inp"
            />
          </Field>
          <Field label="Meetup location" full>
            <input
              value={draft.meet_up}
              onChange={(e) => setDraft({ ...draft, meet_up: e.target.value })}
              placeholder="e.g. Main temple gate, Palitana"
              className="inp"
            />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.is_breakfast}
              onChange={(e) => setDraft({ ...draft, is_breakfast: e.target.checked })}
            />
            Includes breakfast
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.is_guide}
              onChange={(e) => setDraft({ ...draft, is_guide: e.target.checked })}
            />
            Includes guide
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
            />
            Active (shown on the homepage)
          </label>
        </div>

        {/* Features */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Features
            </h4>
            <button
              type="button"
              onClick={() => setDraft({ ...draft, features: [...draft.features, ""] })}
              className="btn-outline py-1 text-xs"
            >
              <Plus className="h-3 w-3" /> Add feature
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {draft.features.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <input
                    value={f}
                    onChange={(e) => setFeature(i, e.target.value)}
                    placeholder="All 31 temples"
                    className="inp"
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setDraft({ ...draft, features: draft.features.filter((_, j) => j !== i) })
                  }
                  title="Remove"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}

function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
